import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ClipboardList, LogOut, Receipt } from "lucide-react";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function CalendarEditor() {
  const [availability, setAvailability] = useState({});
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/availability").then((res) => setAvailability(res.data.availability)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const byStatus = { available: [], full: [], closed: [] };
  Object.entries(availability).forEach(([dateStr, info]) => {
    if (byStatus[info.status]) byStatus[info.status].push(new Date(dateStr + "T00:00:00"));
  });

  const setStatus = async (status) => {
    if (!selected) return toast.error("Click a date first.");
    setSaving(true);
    try {
      await api.put("/admin/availability", { date: toKey(selected), status });
      toast.success(`${toKey(selected)} marked ${status}.`);
      setAvailability((prev) => ({ ...prev, [toKey(selected)]: { status, note: "" } }));
    } catch (e) {
      toast.error(formatApiError(e, "Could not update date."));
    } finally {
      setSaving(false);
    }
  };

  const selectedKey = selected ? toKey(selected) : null;
  const selectedStatus = selectedKey ? availability[selectedKey]?.status : null;

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="calendar-editor">
      <div>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={setSelected}
          modifiers={{ available: byStatus.available, full: byStatus.full, closed: byStatus.closed }}
          modifiersClassNames={{ available: "cal-day-available", full: "cal-day-full", closed: "cal-day-closed" }}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        />
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-sky-400" /> Available</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-300" /> Fully booked</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-slate-200" /> Not running</span>
        </div>
      </div>
      <div className="card-clean h-fit p-6">
        <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-900">Update a Date</h3>
        <p className="mt-1 text-sm text-slate-500">Click a day on the calendar, then set its status. Changes go live instantly.</p>
        <p className="mt-5 text-sm font-semibold text-slate-900" data-testid="editor-selected-date">
          {selected ? selected.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "No date selected"}
          {selectedStatus && <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-bold text-sky-700">{selectedStatus}</span>}
        </p>
        <div className="mt-4 grid gap-2">
          <Button data-testid="editor-set-available" onClick={() => setStatus("available")} disabled={saving}
            className="btn-lift rounded-full bg-sky-500 font-bold text-white hover:bg-sky-600">Open for bookings</Button>
          <Button data-testid="editor-set-full" onClick={() => setStatus("full")} disabled={saving} variant="outline"
            className="rounded-full border-red-200 font-bold text-red-600 hover:bg-red-50">Mark fully booked</Button>
          <Button data-testid="editor-set-closed" onClick={() => setStatus("closed")} disabled={saving} variant="outline"
            className="rounded-full font-bold text-slate-600 hover:bg-slate-100">Not running this day</Button>
        </div>
      </div>
    </div>
  );
}

function BookingsTable() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/admin/bookings").then((res) => setBookings(res.data.bookings)).catch(() => {});
  }, []);
  return (
    <div className="card-clean overflow-x-auto" data-testid="bookings-table">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3">Trip</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Anglers</th>
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {bookings.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="bookings-empty">No bookings yet.</td></tr>
          )}
          {bookings.map((b) => (
            <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`booking-row-${b.id}`}>
              <td className="px-4 py-3 font-medium text-slate-900">{b.label}</td>
              <td className="px-4 py-3 text-slate-600">{b.date}</td>
              <td className="px-4 py-3 text-slate-600">{b.quantity}</td>
              <td className="px-4 py-3 text-slate-600">{b.customer_name || "—"}<br /><span className="text-xs text-slate-400">{b.customer_email}</span></td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                  b.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : b.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                  {b.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTable() {
  const [transactions, setTransactions] = useState([]);
  useEffect(() => {
    api.get("/admin/transactions").then((res) => setTransactions(res.data.transactions)).catch(() => {});
  }, []);
  return (
    <div className="card-clean overflow-x-auto" data-testid="transactions-table">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3">Item</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Payment</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length === 0 && (
            <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400" data-testid="transactions-empty">No transactions yet.</td></tr>
          )}
          {transactions.map((t) => (
            <tr key={t.session_id} className="border-b border-slate-100 last:border-0" data-testid={`transaction-row-${t.session_id.slice(-8)}`}>
              <td className="px-4 py-3 font-medium text-slate-900">{t.label}{t.date ? <span className="block text-xs font-normal text-slate-400">{t.date}</span> : null}</td>
              <td className="px-4 py-3 text-slate-600">£{(t.amount / 100).toFixed(2)}</td>
              <td className="px-4 py-3 text-slate-600">{t.customer_name || "—"}</td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                  t.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {t.payment_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500" data-testid="admin-loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <img src="/shande-logo.png" alt="Shande III logo" className="h-10 w-10 rounded-full object-contain" />
            <span className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-900">
              Shande <span className="text-sky-500">III</span> <span className="ml-2 text-sm font-bold text-slate-400">Skipper Dashboard</span>
            </span>
          </div>
          <Button data-testid="admin-logout-button" variant="outline" onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="rounded-full font-semibold text-slate-600">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <Tabs defaultValue="calendar">
          <TabsList className="bg-white" data-testid="admin-tabs">
            <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays className="mr-2 h-4 w-4" /> Calendar</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings"><ClipboardList className="mr-2 h-4 w-4" /> Bookings</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions"><Receipt className="mr-2 h-4 w-4" /> Orders</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="calendar"><CalendarEditor /></TabsContent>
            <TabsContent value="bookings"><BookingsTable /></TabsContent>
            <TabsContent value="transactions"><TransactionsTable /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
